/**
 * Bridge Fork Yeah PDP pickup day → Bird Pickup & Delivery (V2 SDK).
 * Bird hydrates from sdk.selections.set(), not from ISO cart attributes.
 * Seed Bird only when its date is empty; customer Bird changes sync cart forward
 * and must not be overwritten by ensure/remount.
 * Remount only when Horizon replaces the mount node — remount clears the date.
 */
(function () {
  if (window.FYBirdPickup) return;

  const MONTHS = {
    jan: 0,
    feb: 1,
    mar: 2,
    apr: 3,
    may: 4,
    jun: 5,
    jul: 6,
    aug: 7,
    sep: 8,
    oct: 9,
    nov: 10,
    dec: 11,
  };

  /** Live Temecula kitchen — used when Theme settings location id is blank. */
  const DEFAULT_LOCATION_ID = 115298;
  const DEFAULT_LOCATION_NAME = '45356 Via Jaca';

  const PICKUP_ATTR = 'Pickup day';
  const WARN_ID = 'fy-bird-schedule-warn';

  let writing = false;
  let writingTimer = 0;
  let remountTimer = 0;
  let applyTimer = 0;
  let subscribed = false;
  let lastMountEl = null;
  let lastAppliedIso = '';
  let applying = false;

  /** Hold the writing flag past cart:update so ensure cannot bounce Bird. */
  function beginWriting(ms = 600) {
    writing = true;
    window.clearTimeout(writingTimer);
    writingTimer = window.setTimeout(() => {
      writing = false;
    }, ms);
  }

  function birdCurrentSelection() {
    try {
      return window.BirdApp?.sdk?.selections?.current || null;
    } catch {
      return null;
    }
  }

  function birdHasPickupDate() {
    const current = birdCurrentSelection();
    if (!current) return false;
    const method = String(current.method || '').toLowerCase();
    return method.includes('pick') && Boolean(parseDayLabel(current.date));
  }

  function readConfig() {
    const el = document.getElementById('fy-bird-config');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch {
      return {};
    }
  }

  function parseDayLabel(label) {
    const raw = String(label || '').trim();
    if (!raw) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const m = raw.match(/([A-Za-z]{3})[a-z]*\.?\s+(\d{1,2}),?\s+(\d{4})/i);
    if (!m) return '';
    const month = MONTHS[m[1].slice(0, 3).toLowerCase()];
    if (month == null) return '';
    const day = String(Number(m[2])).padStart(2, '0');
    const mon = String(month + 1).padStart(2, '0');
    return `${m[3]}-${mon}-${day}`;
  }

  function dateFromIso(iso) {
    const parts = String(iso || '').split('-').map(Number);
    if (parts.length !== 3 || parts.some((n) => !n && n !== 0)) return null;
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  function formatPickupLabel(iso) {
    const date = dateFromIso(iso);
    if (!date) return iso || '';
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  /** Bird cartAttributeDateFormat is "MM DD, YYYY" → "Aug 23, 2026". */
  function formatBirdDate(iso) {
    const date = dateFromIso(iso);
    if (!date) return iso || '';
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function formatBirdDay(iso) {
    const date = dateFromIso(iso);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  }

  function unwrapLocations(raw) {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw : raw.locations || raw.latestLocations?.locations || [];
    return (list || [])
      .map((entry) => entry?.location || entry)
      .filter((loc) => loc && loc.id != null);
  }

  async function resolveLocation(sdk) {
    const cfg = readConfig();
    const fromSettings = String(cfg.locationId || '').trim();
    if (fromSettings) {
      const id = Number(fromSettings) || fromSettings;
      return {
        id,
        name: String(cfg.locationName || cfg.locationLabel || DEFAULT_LOCATION_NAME).trim(),
      };
    }
    try {
      let raw = sdk?.pickup?.getLocations?.();
      if (raw && typeof raw.then === 'function') raw = await raw;
      const locs = unwrapLocations(raw) || unwrapLocations(sdk?.locations);
      const first = locs[0];
      if (first) {
        return { id: first.id, name: first.name || first.address1 || DEFAULT_LOCATION_NAME };
      }
    } catch {
      /* fall through */
    }
    return {
      id: DEFAULT_LOCATION_ID,
      name: String(cfg.locationName || DEFAULT_LOCATION_NAME).trim(),
    };
  }

  function locationRecord(sdk, locationId) {
    const locs =
      unwrapLocations(sdk?.pickup?.latestLocations) ||
      unwrapLocations(sdk?.locations) ||
      [];
    return locs.find((l) => String(l.id) === String(locationId)) || locs[0] || null;
  }

  function isDateBlocked(sdk, locationId, iso) {
    const loc = locationRecord(sdk, locationId);
    const blockouts = loc?.pickup?.blockoutSlots;
    if (!Array.isArray(blockouts)) return false;
    const hit = blockouts.find((b) => String(b.date) === String(iso));
    if (!hit) return false;
    // Full-day blackout when no time window (or null/blank times).
    return !hit.startTime && !hit.endTime;
  }

  function firstSlotTime(sdk, locationId, iso) {
    try {
      if (isDateBlocked(sdk, locationId, iso)) return '';
      const loc = locationRecord(sdk, locationId);
      const weekdays = loc?.pickup?.weekdays;
      if (!Array.isArray(weekdays)) return '';
      const date = dateFromIso(iso);
      if (!date) return '';
      const dayCfg = weekdays[date.getDay()];
      const slot = dayCfg?.enabled && dayCfg.slots && dayCfg.slots[0];
      return slot?.startTime || '';
    } catch {
      return '';
    }
  }

  function attributesForDay(dayLabel, overrides = {}) {
    const config = { ...readConfig(), ...overrides };
    const iso = parseDayLabel(dayLabel) || config.todayIso || '';
    if (!iso) return {};

    const locationName = String(
      overrides.locationName || config.locationName || DEFAULT_LOCATION_NAME
    ).trim();
    const locationId = String(
      overrides.locationId || config.locationId || DEFAULT_LOCATION_ID
    ).trim();
    const time = String(overrides.time || '').trim();
    const attrs = {
      'Delivery Method': 'Pick Up',
      'Delivery Date': formatBirdDate(iso),
      'Delivery Day': formatBirdDay(iso),
      'Delivery Location': locationName,
    };
    if (time) attrs['Delivery Time'] = time;
    if (locationId) {
      attrs.locationId = String(locationId);
      const slot = time
        ? `M=P;L=${locationId};D=${iso};T=${time};S=T`
        : `M=P;L=${locationId};D=${iso};S=D`;
      attrs._BirdChimeSlotId = slot;
    }
    return attrs;
  }

  function updateCartAttributes(attributes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes }),
    }).then((r) => {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  function fetchCart() {
    return fetch('/cart.js').then((r) => r.json());
  }

  function uniqueLinePickupDays(cart) {
    const days = new Set();
    (cart?.items || []).forEach((item) => {
      const props = item.properties || {};
      if (props['Pickup day']) days.add(String(props['Pickup day']));
    });
    return days;
  }

  function mealPickupDates(cart) {
    const dates = new Set();
    (cart?.items || []).forEach((item) => {
      const props = item.properties || {};
      const raw = props['_Pit Inventory Date'] || props['Pickup Date'] || props['Pickup day'];
      const iso = parseDayLabel(raw);
      if (iso) dates.add(iso);
    });
    return dates;
  }

  function pickupDayFromCart(cart) {
    const attrs = cart?.attributes || {};
    if (attrs[PICKUP_ATTR]) return attrs[PICKUP_ATTR];
    const lineDays = uniqueLinePickupDays(cart);
    return lineDays.size ? [...lineDays][0] : '';
  }

  function isBirdComplete() {
    const sdk = window.BirdApp?.sdk;
    if (!sdk?.selections) return false;
    try {
      const selection = sdk.selections.current;
      if (!String(selection?.time || '').trim() || !parseDayLabel(selection?.date)) return false;
      if (typeof sdk.selections.canCheckout === 'function') {
        return Boolean(sdk.selections.canCheckout()?.canProceed);
      }
      const current = sdk.selections.current;
      return Boolean(
        current?.method &&
          String(current.method).toLowerCase().includes('pick') &&
          current.date && current.time
      );
    } catch {
      return false;
    }
  }

  function setScheduleWarning(message) {
    const hosts = [
      document.getElementById('fy-bird-widget'),
      document.querySelector('.cart-drawer__summary'),
      document.querySelector('.cart-summary__inner'),
      document.querySelector('.cart-page__summary'),
    ].filter(Boolean);

    document.querySelectorAll(`#${WARN_ID}`).forEach((el) => el.remove());
    if (!message || !hosts.length) return;

    const note = document.createElement('p');
    note.id = WARN_ID;
    note.className = 'fy-cart-pickup-note fy-cart-pickup-note--mixed';
    note.setAttribute('role', 'status');
    note.textContent = message;
    hosts[0].parentElement?.insertBefore(note, hosts[0]);
  }

  async function syncShopPay() {
    const birdPresent = Boolean(window.BirdApp);
    let show = false;
    if (birdPresent) {
      show = isBirdComplete();
    } else {
      try {
        const cart = await fetchCart();
        const method = String(cart?.attributes?.['Delivery Method'] || '').toLowerCase();
        show =
          method.includes('pick') &&
          Boolean(String(cart?.attributes?.['Delivery Time'] || '').trim()) &&
          Boolean(
            parseDayLabel(cart?.attributes?.[PICKUP_ATTR] || cart?.attributes?.['Delivery Date'])
          );
      } catch {
        show = false;
      }
    }
    document.querySelectorAll('[data-fy-shop-pay]').forEach((el) => {
      el.hidden = !show;
    });
  }

  async function waitForBird(timeoutMs) {
    if (window.BirdApp?.ready) return window.BirdApp.ready;
    return new Promise((resolve) => {
      const start = Date.now();
      const timer = setInterval(() => {
        if (window.BirdApp?.ready) {
          clearInterval(timer);
          window.BirdApp.ready.then(resolve).catch(() => resolve(null));
          return;
        }
        if (Date.now() - start > timeoutMs) {
          clearInterval(timer);
          resolve(null);
        }
      }, 200);
    });
  }


  async function applyBirdSelection(iso, options = {}) {
    if (!iso || applying) return null;
    const sdk = window.BirdApp?.sdk;
    if (!sdk?.selections?.set) return null;

    applying = true;
    try {
      await window.BirdApp.ready;
      const location = await resolveLocation(sdk);
      const blocked = isDateBlocked(sdk, location.id, iso);
      if (blocked) {
        setScheduleWarning(
          'That pickup day isn’t open in scheduling — pick another Friday–Sunday in the calendar.'
        );
      }

      const current = sdk.selections.current || {};
      const already =
        parseDayLabel(current.date) === iso &&
        String(current.method || '').toLowerCase().includes('pick') &&
        (!location.id || String(current.locationId) === String(location.id)) &&
        (current.time || current.slotType !== 'datetime');
      if (already && !options.force) {
        lastAppliedIso = iso;
        if (!blocked) setScheduleWarning('');
        await syncShopPay();
        return current;
      }

      const payload = {
        method: 'Pickup',
        date: iso,
      };
      if (location.id) payload.locationId = Number(location.id) || location.id;

      let time = options.time || firstSlotTime(sdk, payload.locationId, iso);
      if (!time && !blocked) {
        try {
          let slots = sdk.pickup?.getSlots?.({ locationId: payload.locationId, date: iso });
          if (slots && typeof slots.then === 'function') slots = await slots;
          const days = slots?.days || [];
          const day = days.find((d) => parseDayLabel(d.date || d.day) === iso) || days[0];
          const first = day?.slots?.[0] || day?.times?.[0];
          time = first?.startTime || first?.time || (typeof first === 'string' ? first : '');
        } catch {
          time = '';
        }
      }
      if (time) payload.time = time;

      sdk.selections.set(payload);
      if (typeof sdk.selections.save === 'function') {
        const saved = sdk.selections.save();
        if (saved && typeof saved.then === 'function') await saved;
      }

      // Give the widget a tick to paint, then confirm.
      await new Promise((r) => setTimeout(r, 50));
      const after = sdk.selections.current || {};
      const applied = parseDayLabel(after.date) === iso;
      lastAppliedIso = applied ? iso : lastAppliedIso;

      if (!applied || blocked) {
        setScheduleWarning(
          'That pickup day isn’t open in scheduling — pick another Friday–Sunday in the calendar.'
        );
      } else {
        setScheduleWarning('');
      }

      await syncShopPay();
      return after;
    } catch {
      return null;
    } finally {
      applying = false;
    }
  }

  async function remountBird() {
    const app = window.BirdApp;
    if (!app) {
      await syncShopPay();
      return;
    }
    try {
      await app.ready;
      const mount = document.getElementById('fy-bird-widget');
      const widget = app.widget;
      const mounted = typeof widget?.isMounted === 'function' ? widget.isMounted() : false;
      const nodeChanged = Boolean(mount && lastMountEl && mount !== lastMountEl);
      if (mount && !nodeChanged && mounted) {
        subscribeBird();
        // Only seed if Bird lost its date — never overwrite a customer pick.
        if (!birdHasPickupDate() && lastAppliedIso) {
          await applyBirdSelection(lastAppliedIso);
        }
        await syncShopPay();
        return;
      }
      lastMountEl = mount;
      if (mount && widget?.mount && !mounted) {
        await widget.mount(mount);
      } else if (widget?.remount && (nodeChanged || !mounted)) {
        widget.remount();
      }
    } catch {
      /* Bird embed may not be enabled yet */
    }
    subscribeBird();
    if (!birdHasPickupDate() && lastAppliedIso) {
      await applyBirdSelection(lastAppliedIso, { force: true });
    }
    await syncShopPay();
  }

  function scheduleRemount() {
    window.clearTimeout(remountTimer);
    remountTimer = window.setTimeout(() => {
      remountBird();
    }, 250);
  }

  function scheduleEnsure() {
    window.clearTimeout(applyTimer);
    applyTimer = window.setTimeout(() => {
      ensureCartPickup().catch(() => {});
    }, 300);
  }

  async function writeBirdForDay(dayLabel, options = {}) {
    const iso = parseDayLabel(dayLabel);
    if (!iso) return null;

    let cart = options.cart;
    if (!cart) {
      try {
        cart = await fetchCart();
      } catch {
        cart = null;
      }
    }

    await waitForBird(8000);
    const location = window.BirdApp?.sdk
      ? await resolveLocation(window.BirdApp.sdk)
      : { id: readConfig().locationId || DEFAULT_LOCATION_ID, name: readConfig().locationName || DEFAULT_LOCATION_NAME };

    const selection = await applyBirdSelection(iso, options);
    const time = selection?.time || '';

    const attrs = attributesForDay(dayLabel, {
      locationId: location.id,
      locationName: location.name,
      time,
    });
    if (options.includePickupDay !== false) attrs[PICKUP_ATTR] = formatPickupLabel(iso);

    beginWriting(800);
    try {
      if (Object.keys(attrs).length) {
        cart = await updateCartAttributes(attrs);
      }
      if (window.BirdApp?.sdk?.setCart && cart) {
        try {
          window.BirdApp.sdk.setCart(cart);
        } catch {
          /* ignore */
        }
      }
      // Re-apply after attribute write — setCart can clear Bird's date.
      await applyBirdSelection(iso, { time, force: true });
      return cart;
    } catch {
      writing = false;
      return null;
    }
  }

  async function ensureCartPickup() {
    if (writing || applying) return;

    let cart;
    try {
      cart = await fetchCart();
    } catch {
      return;
    }
    if (!cart?.item_count && !document.getElementById('fy-bird-widget')) return;

    await waitForBird(4000);

    const mealDates = mealPickupDates(cart);
    if (mealDates.size > 1) {
      setScheduleWarning('Meals in this cart have different pickup dates. Please use the same pickup date for every meal.');
      await syncShopPay();
      return;
    }
    const requiredIso = mealDates.size === 1 ? [...mealDates][0] : '';
    if (requiredIso) {
      const currentIso = parseDayLabel(birdCurrentSelection()?.date);
      lastAppliedIso = requiredIso;
      if (currentIso !== requiredIso) {
        await writeBirdForDay(requiredIso, { cart, force: true, includePickupDay: true });
      } else {
        await syncShopPay();
      }
      return;
    }

    if (birdHasPickupDate()) {
      const current = birdCurrentSelection();
      lastAppliedIso = parseDayLabel(current?.date) || lastAppliedIso;
      await syncShopPay();
      return;
    }

    const day = pickupDayFromCart(cart);
    if (day) {
      lastAppliedIso = parseDayLabel(day) || lastAppliedIso;
      // Seed empty Bird only; force is safe here because date is blank.
      await writeBirdForDay(day, { cart, force: true, includePickupDay: true });
      return;
    }

    if (!cart?.item_count) return;
    const config = readConfig();
    if (config.todayOk !== '1') return;
    const todayLabel = config.todayLabel || formatPickupLabel(config.todayIso);
    await writeBirdForDay(todayLabel, { cart, includePickupDay: false, force: true });
  }

  async function syncPickupDayFromBird(selection) {
    if (writing || applying) return;
    const iso = parseDayLabel(selection?.date);
    const method = String(selection?.method || '').toLowerCase();
    if (!iso || !method.includes('pick')) return;

    lastAppliedIso = iso;
    setScheduleWarning('');
    let cart;
    try {
      cart = await fetchCart();
    } catch {
      return;
    }
    const mealDates = mealPickupDates(cart);
    if (mealDates.size > 1) {
      setScheduleWarning('Meals in this cart have different pickup dates. Please use the same pickup date for every meal.');
      await syncShopPay();
      return;
    }
    const requiredIso = mealDates.size === 1 ? [...mealDates][0] : '';
    if (requiredIso && iso !== requiredIso) {
      setScheduleWarning('Your meal is reserved for ' + formatPickupLabel(requiredIso) + '. Choose a pickup time for that date.');
      lastAppliedIso = requiredIso;
      await writeBirdForDay(requiredIso, { cart, force: true, includePickupDay: true });
      return;
    }

    const label = formatPickupLabel(iso);
    const currentIso = parseDayLabel(cart.attributes?.[PICKUP_ATTR]);
    const deliveryIso = parseDayLabel(cart.attributes?.['Delivery Date']);
    if (currentIso === iso && deliveryIso === iso) {
      await syncShopPay();
      return;
    }

    const location = window.BirdApp?.sdk
      ? await resolveLocation(window.BirdApp.sdk)
      : {
          id: readConfig().locationId || DEFAULT_LOCATION_ID,
          name: readConfig().locationName || DEFAULT_LOCATION_NAME,
        };
    const attrs = attributesForDay(label, {
      locationId: location.id,
      locationName: location.name,
      time: selection?.time || '',
    });
    attrs[PICKUP_ATTR] = label;

    beginWriting(800);
    try {
      await updateCartAttributes(attrs);
    } catch {
      writing = false;
    }
    await syncShopPay();
  }

  function subscribeBird() {
    if (subscribed) return;
    const sdk = window.BirdApp?.sdk;
    if (!sdk?.subscribe && !sdk?.selections?.subscribe) return;
    subscribed = true;
    try {
      const sub = sdk.subscribe || sdk.selections.subscribe.bind(sdk.selections);
      sub((state) => {
        syncShopPay();
        const selection = state?.selection || sdk.selections?.current;
        if (selection) syncPickupDayFromBird(selection);
      });
    } catch {
      subscribed = false;
    }
  }

  function watchMountNode() {
    const roots = [
      document.getElementById('cart-drawer'),
      document.querySelector('main'),
      document.body,
    ].filter(Boolean);

    roots.forEach((root) => {
      if (root.dataset.fyBirdWatch === '1') return;
      root.dataset.fyBirdWatch = '1';
      new MutationObserver(() => {
        const mount = document.getElementById('fy-bird-widget');
        if (!mount) return;
        // Only remount when Horizon replaces the node. Do not re-hydrate on every morph.
        if (mount !== lastMountEl) scheduleRemount();
      }).observe(root, { childList: true, subtree: true });
    });
  }

  function onPickupDay(event) {
    const day = event?.detail?.day;
    if (!day) return;
    lastAppliedIso = parseDayLabel(day) || lastAppliedIso;
    // PDP day change is authoritative — force Bird to match.
    writeBirdForDay(day, { force: true, includePickupDay: true }).catch(() => {});
  }

  function onDrawerOpen(event) {
    const target = event?.target;
    if (target && target.id && target.id !== 'cart-drawer') return;
    scheduleEnsure();
  }

  function onCartUpdate() {
    if (writing || applying) return;
    if (birdHasPickupDate()) {
      syncShopPay();
      return;
    }
    scheduleEnsure();
  }

  // Fail closed at the storefront checkout boundary. This never mutates cart lines
  // or Bird selections; payment methods remain available on Shopify checkout.
  function mealCartError(cart) {
    const rules = new Map();
    document.querySelectorAll('[data-fy-meal-rules]').forEach((el) => {
      try { JSON.parse(el.textContent || '[]').forEach((rule) => rules.set(String(rule.id), rule)); }
      catch { /* Missing rules are handled below. */ }
    });
    for (const item of cart.items || []) {
      const rule = rules.get(String(item.product_id));
      if (!rule) return 'Your cart changed. Refresh the cart before checking out.';
      if (!rule.meats) continue;
      const props = item.properties || {};
      const required = ['Sauce Choice', 'Utensils', 'Includes', 'Portion', '_Sauce SKU', '_Includes SKU'];
      const quantities = ['_Sauce qty', '_Includes qty'];
      for (const [kind, count] of [['Meat', rule.meats], ['Side', rule.sides]]) {
        for (let i = 1; i <= count; i++) {
          required.push(kind + ' ' + i, '_' + kind + ' ' + i + ' SKU');
          quantities.push('_' + kind + ' ' + i + ' qty');
        }
      }
      if (required.some((key) => !String(props[key] || '').trim()) ||
          quantities.some((key) => !Number.isFinite(Number(props[key])) || Number(props[key]) <= 0)) {
        return (item.product_title || item.title || 'A meal') + ' is missing meal choices. Open that meal, select all choices, and replace the incomplete line before checkout. Your other cart items have not been changed.';
      }
    }
    return '';
  }

  // Compare equivalent 12-hour/24-hour representations without accepting stale slots.
  function normalizedPickupTime(value) {
    const parts = String(value || '').trim().replace(/[–—]/g, '-').split(/\s*-\s*/);
    if (parts.length < 1 || parts.length > 2) return '';
    const normalized = parts.map((part) => {
      const match = part.trim().match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
      if (!match) return '';
      let hour = Number(match[1]);
      const minute = Number(match[2]);
      if (minute > 59 || (match[3] ? hour < 1 || hour > 12 : hour > 23)) return '';
      if (match[3]) hour = hour % 12 + (match[3].toUpperCase() === 'PM' ? 12 : 0);
      return String(hour).padStart(2, '0') + ':' + String(minute).padStart(2, '0');
    });
    if (normalized.some((part) => !part)) return '';
    return normalized.length === 1 || normalized[0] === normalized[1] ? normalized[0] : normalized.join('-');
  }

  let checkoutChecking = false;
  let checkoutReplay = false;
  async function guardCheckout(event) {
    if (checkoutReplay) return;
    const target = event.target instanceof Element ? event.target : null;
    const button = event.type === 'click' ? target?.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"]') : event.submitter;
    const form = event.type === 'submit' ? target : button?.form;
    if (event.type === 'submit' && (!form?.matches('form.cart-form') || (button && button.name !== 'checkout'))) return;
    if (event.type === 'click' && !button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutChecking) return;
    checkoutChecking = true;
    try {
      const cart = await fetchCart();
      let error = mealCartError(cart);
      const mealDates = mealPickupDates(cart);
      if (!error && mealDates.size > 1) error = 'Meals in this cart have different pickup dates. Please use the same pickup date for every meal.';
      const requiredIso = mealDates.size === 1 ? [...mealDates][0] : '';
      const selection = birdCurrentSelection();
      if (!error && requiredIso && parseDayLabel(selection?.date) !== requiredIso) error = 'Pickup must stay on ' + formatPickupLabel(requiredIso) + ', the date reserved with your meal.';
      const attrs = cart.attributes || {};
      if (!error && (!isBirdComplete() ||
          parseDayLabel(attrs['Delivery Date']) !== parseDayLabel(selection?.date) ||
          !normalizedPickupTime(attrs['Delivery Time']) ||
          normalizedPickupTime(attrs['Delivery Time']) !== normalizedPickupTime(selection?.time))) {
        error = !isBirdComplete() ? 'Pickup selection is not ready. Please choose an available date and time.' : parseDayLabel(attrs['Delivery Date']) !== parseDayLabel(selection?.date) ? 'Pickup date is not saved yet. Please wait and try again.' : !String(attrs['Delivery Time'] || '').trim() ? 'Pickup time is not saved yet. Please choose a time and try again.' : 'Pickup time does not match the saved time. Please reselect your time.';
      }
      if (error) {
        setScheduleWarning(error);
        document.getElementById(WARN_ID)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      setScheduleWarning('');
      checkoutReplay = true;
      try {
        if (event.type === 'click') button.click();
        else form.requestSubmit(button || undefined);
      } finally { checkoutReplay = false; }
    } catch {
      setScheduleWarning('We could not verify your meal choices and pickup time. Please refresh the cart and try again. Nothing was removed.');
    } finally { checkoutChecking = false; }
  }

  function init() {
    document.addEventListener('click', guardCheckout, true);
    document.addEventListener('submit', guardCheckout, true);
    document.addEventListener('fy:pickup-day', onPickupDay);
    document.addEventListener('theme-drawer:open', onDrawerOpen);
    document.addEventListener('bird:widget:initialized', () => {
      subscribeBird();
      scheduleEnsure();
    });
    document.addEventListener('bird:widget:reinitialized', () => {
      scheduleRemount();
    });
    window.addEventListener('bird:selection:changed', (event) => {
      const selection = event?.detail?.selection;
      if (selection) syncPickupDayFromBird(selection);
      else syncShopPay();
    });
    document.addEventListener('cart:update', onCartUpdate);

    watchMountNode();
    waitForBird(12000).then(() => {
      subscribeBird();
      scheduleEnsure();
    });

    if (window.location.pathname.includes('/cart') || document.getElementById('fy-bird-widget')) {
      scheduleEnsure();
    }
  }

  window.FYBirdPickup = {
    parseDayLabel,
    formatPickupLabel,
    attributesForDay,
    writeBirdForDay,
    remount: remountBird,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* Fork Yeah Game Day Pack: future-date gate and exact pit inventory metadata. */
(function () {
  if (!/^\/products\/game-day-pack-feeds-6-8\/?$/.test(location.pathname)) return;
    var NEED = { brisket: 3, pulledPork: 3, ribs: 3 };
      var availability = {};
        var selectedDate = sessionStorage.getItem('fyPickupDate') || '';
          var root;
            function todayLocal() { var d = new Date(); return [d.getFullYear(), String(d.getMonth()+1).padStart(2,'0'), String(d.getDate()).padStart(2,'0')].join('-'); }
              function dateLabel(iso) { return new Intl.DateTimeFormat('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric',timeZone:'UTC'}).format(new Date(iso+'T12:00:00Z')); }
                function status(message, error) { var n = root && root.querySelector('[data-pack-status]'); if (n) { n.textContent=message; n.style.color=error?'#9b1c1c':'inherit'; } }
                  function addInput(form, name, value) { var input=form.querySelector('input[name="'+name+'"]'); if(!input){input=document.createElement('input');input.type='hidden';input.name=name;form.appendChild(input);} input.value=value; }
                    function decorateForms() {
                        document.querySelectorAll('form[action*="/cart/add"]').forEach(function(form){
                              addInput(form,'properties[Pickup Date]',selectedDate);
                                    addInput(form,'properties[_Pit Inventory Date]',selectedDate);
                                          addInput(form,'properties[Serves]','6–8 guests');
                                                addInput(form,'properties[Brisket]','1.5 lb USDA Prime brisket');
                                                      addInput(form,'properties[Pulled Pork]','1.5 lb pulled pork');
                                                            addInput(form,'properties[Ribs]','1 full rack St. Louis ribs');
                                                                  addInput(form,'properties[Mac & Cheese]','1 quart');
                                                                        addInput(form,'properties[Pit Beans]','1 quart');
                                                                              addInput(form,'properties[Signature Potato Salad]','1 quart');
                                                                                    addInput(form,'properties[Honey Butter Cornbread]','6 pieces');
                                                                                          addInput(form,'properties[Sauce]',(root && root.querySelector('[data-pack-sauce]') || {}).value || '');
                                                                                                addInput(form,'properties[_Pit Inventory brisket Parts]','3');
                                                                                                      addInput(form,'properties[_Pit Inventory pulledPork Parts]','3');
                                                                                                            addInput(form,'properties[_Pit Inventory ribs Parts]','3');
                                                                                                                });
                                                                                                                  }
                                                                                                                    function meatKey(value) { var t=String(value||'').toLowerCase(); if(t.indexOf('brisket')>=0)return'brisket'; if(t.indexOf('pulled pork')>=0)return'pulledPork'; if(t.indexOf('rib')>=0)return'ribs'; if(t.indexOf('tri-tip')>=0||t.indexOf('tri tip')>=0)return'triTip'; return null; }
                                                                                                                      async function cartParts(date) {
                                                                                                                          var used={}; var cart=await fetch('/cart.js',{headers:{Accept:'application/json'}}).then(function(r){return r.json();});
                                                                                                                              (cart.items||[]).forEach(function(item){ var p=item.properties||{}; if((p['Pickup Date']||p['_Pit Inventory Date'])!==date)return; var explicit=false;
                                                                                                                                    Object.keys(NEED).forEach(function(k){var v=Number(p['_Pit Inventory '+k+' Parts']);if(Number.isFinite(v)){used[k]=(used[k]||0)+v*Number(item.quantity||1);explicit=true;}});
                                                                                                                                          if(!explicit)Object.keys(p).forEach(function(n){if(!/^Meat \d+$/i.test(n))return;var k=meatKey(p[n]);if(k)used[k]=(used[k]||0)+Number(item.quantity||1);});
                                                                                                                                              }); return used;
                                                                                                                                                }
                                                                                                                                                  async function dateFits(date) { var day=availability[date]; if(!day)return false; var used=await cartParts(date); return Object.keys(NEED).every(function(k){return day[k] && day[k].remainingParts-(used[k]||0)>=NEED[k];}); }
                                                                                                                                                    async function validate() { var sauce=root.querySelector('[data-pack-sauce]').value; if(!selectedDate){status('Choose a future pickup date before adding this pack.',true);return false;} if(!(await dateFits(selectedDate))){status('This pack is no longer available for that pickup date. Please choose another date.',true);return false;} if(!sauce){status('Choose the pint of sauce for this pack.',true);return false;} decorateForms();status('Pickup: '+dateLabel(selectedDate)+'. Inventory is available for this pack.',false);return true; }
                                                                                                                                                      async function setup() {
                                                                                                                                                          var form=document.querySelector('form[action*="/cart/add"]'); if(!form)return setTimeout(setup,150);
                                                                                                                                                              root=document.createElement('div');root.className='fy-game-day-pack';root.style.cssText='margin:1.25rem 0;padding:1.1rem;border:1px solid rgba(128,128,128,.35);border-radius:8px';
                                                                                                                                                                  root.innerHTML='<strong style="display:block;margin-bottom:.4rem">Preorder pickup</strong><label for="fy-pack-date" style="display:block;margin:.55rem 0 .25rem">Choose a future pickup date</label><select id="fy-pack-date" data-pack-date required style="width:100%;padding:.7rem"><option value="">Select an available Friday–Sunday…</option></select><label for="fy-pack-sauce" style="display:block;margin:.8rem 0 .25rem">Choose your pint of sauce</label><select id="fy-pack-sauce" data-pack-sauce required style="width:100%;padding:.7rem"><option value="">Select sauce…</option><option>Original Sauce</option><option>Forking Hot Sauce</option></select><small data-pack-status style="display:block;margin-top:.65rem">Loading available preorder dates…</small>';
                                                                                                                                                                      var submit=form.querySelector('button[type="submit"],input[type="submit"]');(submit&&submit.parentElement?submit.parentElement:form).before(root);
                                                                                                                                                                          try { var result=await fetch('/apps/pit-inventory',{cache:'no-store',headers:{Accept:'application/json'}}).then(function(r){if(!r.ok)throw Error();return r.json();});availability=result.dates||{};var picker=root.querySelector('[data-pack-date]');
                                                                                                                                                                                for (var date of Object.keys(availability).sort()) if(date>todayLocal() && await dateFits(date)) picker.add(new Option(dateLabel(date),date));
                                                                                                                                                                                      if(selectedDate>todayLocal() && availability[selectedDate] && await dateFits(selectedDate))picker.value=selectedDate;else selectedDate='';
                                                                                                                                                                                            picker.addEventListener('change',async function(){selectedDate=picker.value;sessionStorage.setItem('fyPickupDate',selectedDate);if(selectedDate)await fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes:{'Pickup Date':selectedDate}})});decorateForms();await validate();});
                                                                                                                                                                                                  root.querySelector('[data-pack-sauce]').addEventListener('change',decorateForms);decorateForms();status(picker.options.length>1?'Choose a future pickup date to reserve this pack.':'No future pickup dates currently have enough pit inventory for this pack.',picker.options.length<2);
                                                                                                                                                                                                      } catch(e){status('Pickup-day inventory is temporarily unavailable. Please refresh before ordering.',true);}
                                                                                                                                                                                                        }
                                                                                                                                                                                                          function submitPackOnce(form,event){
  event.preventDefault();
  event.stopImmediatePropagation();
  validate().then(function(ok){
    if(!ok)return;
    decorateForms();
    var quantity=form.querySelector('[name="quantity"]');
    if(quantity)quantity.value='1';
    HTMLFormElement.prototype.submit.call(form);
  });
}
document.addEventListener('click',function(event){
  var button=event.target.closest&&event.target.closest('button[type="submit"]');
  var form=button&&button.form;
  if(!(form instanceof HTMLFormElement)||!form.action.includes('/cart/add'))return;
  submitPackOnce(form,event);
},true);
document.addEventListener('submit',function(event){
  var form=event.target;
  if(!(form instanceof HTMLFormElement)||!form.action.includes('/cart/add'))return;
  submitPackOnce(form,event);
},true);
    setup();
                                                                                                                                                                                                              })();
                                                                                                                                                                                                              



                                                                                                                                                                                                                                              /* Keep one pack selector if the global pickup asset is included more than once. */
                                                                                                                                                                                                                                              (function () {
                                                                                                                                                                                                                                                if (!/^\/products\/game-day-pack-feeds-6-8\/?$/.test(location.pathname)) return;
                                                                                                                                                                                                                                                  var panels = document.querySelectorAll('.fy-game-day-pack');
                                                                                                                                                                                                                                                    for (var i = 1; i < panels.length; i += 1) panels[i].remove();
                                                                                                                                                                                                                                                    })();


                                                                                                                                                                                                                                                    (function(){if(!/^\/products\/game-day-pack-feeds-6-8\/?$/.test(location.pathname))return;var p=document.querySelectorAll('.fy-game-day-pack');for(var i=1;i<p.length;i++)p[i].remove();})();

                                                                                                                                                                                                                                                    /* Game Day Pack: offer a split pint without persistent DOM observers. */
                                                                                                                                                                                                                                                    (function () {
                                                                                                                                                                                                                                                      if (!/^\/products\/game-day-pack-feeds-6-8\/?$/.test(location.pathname)) return;
                                                                                                                                                                                                                                                        var attempts = 0;
                                                                                                                                                                                                                                                          var timer = setInterval(function () {
                                                                                                                                                                                                                                                              attempts += 1;
                                                                                                                                                                                                                                                                  var selects = document.querySelectorAll('[data-pack-sauce]');
                                                                                                                                                                                                                                                                      selects.forEach(function (select) {
                                                                                                                                                                                                                                                                            var both = 'Both Sauces — ½ pint Original + ½ pint Forking Hot';
                                                                                                                                                                                                                                                                                  if (!Array.from(select.options).some(function (option) { return option.value === both; })) {
                                                                                                                                                                                                                                                                                          select.add(new Option(both, both));
                                                                                                                                                                                                                                                                                                }
                                                                                                                                                                                                                                                                                                      if (!select.dataset.packBothSauces) {
                                                                                                                                                                                                                                                                                                              select.dataset.packBothSauces = 'true';
                                                                                                                                                                                                                                                                                                                      select.addEventListener('change', function () {
                                                                                                                                                                                                                                                                                                                                var panel = select.closest('.fy-game-day-pack');
                                                                                                                                                                                                                                                                                                                                          var date = panel && panel.querySelector('[data-pack-date]');
                                                                                                                                                                                                                                                                                                                                                    var message = panel && panel.querySelector('[data-pack-status]');
                                                                                                                                                                                                                                                                                                                                                              if (message && date && date.value && select.value) {
                                                                                                                                                                                                                                                                                                                                                                          message.textContent = 'Selections ready. Click Reserve to add this preorder pack.';
                                                                                                                                                                                                                                                                                                                                                                                      message.style.color = 'inherit';
                                                                                                                                                                                                                                                                                                                                                                                                }
                                                                                                                                                                                                                                                                                                                                                                                                        });
                                                                                                                                                                                                                                                                                                                                                                                                              }
                                                                                                                                                                                                                                                                                                                                                                                                                  });
                                                                                                                                                                                                                                                                                                                                                                                                                      if (selects.length || attempts >= 50) clearInterval(timer);
                                                                                                                                                                                                                                                                                                                                                                                                                        }, 100);
                                                                                                                                                                                                                                                                                                                                                                                                                        })();
                                                                                                                                                                                                                                                                                                                                                                                                                        
                                                                                                                                                                                                                                                                                                                                                                                                                        /* Game Day Pack must be configured on its product page. */
                                                                                                                                                                                                                                                                                                                                                                                                                        (function () {
                                                                                                                                                                                                                                                                                                                                                                                                                          document.addEventListener('submit', function (event) {
                                                                                                                                                                                                                                                                                                                                                                                                                              var form = event.target;
                                                                                                                                                                                                                                                                                                                                                                                                                                  if (!form || !form.matches('form[action*="/cart/add"]')) return;
                                                                                                                                                                                                                                                                                                                                                                                                                                      if (/^\/products\/game-day-pack-feeds-6-8\/?$/.test(location.pathname)) return;
                                                                                                                                                                                                                                                                                                                                                                                                                                          var card = form.closest('[class*="product-card"], li, article') || form.parentElement;
                                                                                                                                                                                                                                                                                                                                                                                                                                              if (!card || !card.querySelector('a[href*="/products/game-day-pack-feeds-6-8"]')) return;
                                                                                                                                                                                                                                                                                                                                                                                                                                                  event.preventDefault();
                                                                                                                                                                                                                                                                                                                                                                                                                                                      event.stopImmediatePropagation();
                                                                                                                                                                                                                                                                                                                                                                                                                                                          location.href = '/products/game-day-pack-feeds-6-8';
                                                                                                                                                                                                                                                                                                                                                                                                                                                            }, true);
                                                                                                                                                                                                                                                                                                                                                                                                                                                            })();
                                                                                                                                                                                                                                                                                                                                                                                                                                                            

/* Fork Yeah homepage Game Day Pack feature. */
(function () {
  if (location.pathname !== '/') return;

  function mountGameDayFeature() {
    if (document.getElementById('fy-game-day-home-feature')) return true;

    var collectionLink = document.querySelector('a[href*="/collections/dinner-platters"]');
    var target = collectionLink && collectionLink.closest('.shopify-section');
    if (!target) return false;

    if (!document.getElementById('fy-game-day-home-style')) {
      var style = document.createElement('style');
      style.id = 'fy-game-day-home-style';
      style.textContent = [
        '#fy-game-day-home-feature{background}}:#21120d;color:#f7ead7;padding:42px 20px;text-align:center;border-top:1px solid #6f3f29;border-bottom:1px solid #6f3f29}',
        '#fy-game-day-home-feature .fy-game-day-inner{max-width:900px;margin:0 auto}',
        '#fy-game-day-home-feature .fy-game-day-kicker{letter-spacing:.14em;text-transform:uppercase;color:#d9a45f;font-size:.85rem;font-weight:700}',
        '#fy-game-day-home-feature h2{margin:8px 0 10px;font-size:clamp(2rem,6vw,4rem);line-height:1;text-transform:uppercase}',
        '#fy-game-day-home-feature p{margin:0 auto 20px;max-width:720px;font-size:1.05rem;line-height:1.6}',
        '#fy-game-day-home-feature .fy-game-day-price{font-size:1.35rem;font-weight:700;margin-bottom:22px}',
        '#fy-game-day-home-feature a{display:inline-block;background:#c56b2d;color:#fff;text-decoration:none;padding:14px 24px;border-radius:3px;font-weight:800;letter-spacing:.06em;text-transform:uppercase}',
        '#fy-game-day-home-feature a:hover{background:#e18440}'
      ].join('');
      document.head.appendChild(style);
    }

    var section = document.createElement('section');
    section.id = 'fy-game-day-home-feature';
    section.innerHTML =
      '<div class="fy-game-day-inner">' +
        '<div class="fy-game-day-kicker">New • Preorder only</div>' +
        '<h2>Game Day Pack</h2>' +
        '<p>Feed 6–8 with 1.5 lb brisket, 1.5 lb pulled pork, a full rack of ribs, three quart-size sides, six cornbreads, and a pint of sauce.</p>' +
        '<div class="fy-game-day-price">$169</div>' +
        '<a href="/products/game-day-pack-feeds-6-8">Choose pickup date &amp; sauce</a>' +
      '</div>';

          var main = document.getElementById('MainContent') || document.querySelector('main');
                if (!main) return false;
                      setTimeout(function () { if (section.parentNode === main) main.insertBefore(section, main.firstElementChild); }, 100);
    return true;
  }

  function start() {
    if (mountGameDayFeature()) return;
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (mountGameDayFeature() || attempts >= 40) clearInterval(timer);
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();


/* Keep the Game Day feature with the homepage product collections, not the header navigation. */
(function () {
  if (location.pathname !== '/') return;

  function positionGameDayFeature() {
    var feature = document.getElementById('fy-game-day-home-feature');
    var target = Array.from(document.querySelectorAll('.shopify-section')).find(function (section) {
      return /what are you craving/i.test(section.textContent || '');
    });
    if (!feature || !target) return false;
    if (feature.nextElementSibling !== target) target.parentNode.insertBefore(feature, target);
    return true;
  }

  if (!positionGameDayFeature()) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (positionGameDayFeature() || attempts >= 40) clearInterval(timer);
    }, 250);
  }
})();


/* The Game Day Pack picker provides live status above Reserve; hide the obsolete static warning below it. */
(function () {
  if (location.pathname !== '/products/game-day-pack-feeds-6-8') return;
  function hideGenericPickupWarning() {
    var note = document.querySelector('[data-fy-pickup-variant-note]');
    if (!note) return false;
    note.hidden = true;
    note.style.setProperty('display', 'none', 'important');
    return true;
  }
  if (!hideGenericPickupWarning()) {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (hideGenericPickupWarning() || tries >= 40) clearInterval(timer);
    }, 250);
  }
})();


/* Thanksgiving turkey preorder pickup windows for 2026. */
(function () {
  if (location.pathname !== '/products/thanksgiving-whole-turkey-preorder') return;
    if (window.__FY_TURKEY_PICKUP_GATE_ACTIVE__) return;
  window.__FY_TURKEY_PICKUP_GATE_ACTIVE__ = true;

  var selected = null;
  var pickupOptions = {
    '2026-11-25': { dateLabel: 'Wednesday, November 25, 2026', window: '3:00 PM – 6:00 PM' },
    '2026-11-26': { dateLabel: 'Thursday, November 26, 2026', window: '8:00 AM – 11:00 AM' }
  };

  function forms() {
    return Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
  }

  function setProperty(form, name, value) {
    var input = form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    }
    input.value = value;
  }

  function applyPickupProperties() {
    if (!selected) return;
    forms().forEach(function (form) {
      setProperty(form, 'properties[Pickup Date]', selected.dateLabel);
      setProperty(form, 'properties[Pickup Window]', selected.window);
      setProperty(form, 'properties[_Thanksgiving Pickup Date]', selected.iso);
      setProperty(form, 'properties[_Thanksgiving Pickup Window]', selected.window);
    });
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { 'Pickup Date': selected.dateLabel, 'Pickup Window': selected.window } })
    }).catch(function () {});
  }

  function showStatus(message, error) {
    var status = document.getElementById('fy-turkey-pickup-status');
    if (!status) return;
    status.textContent = message;
    status.style.color = error ? '#b42318' : '#286529';
  }

  function mount() {
    if (document.getElementById('fy-turkey-pickup-gate')) return true;
    var primaryForm = forms().find(function (form) { return form.offsetParent !== null; }) || forms()[0];
    if (!primaryForm || !primaryForm.parentNode) return false;

    var gate = document.createElement('div');
    gate.id = 'fy-turkey-pickup-gate';
    gate.style.cssText = 'margin:18px 0;padding:16px;border:1px solid #d8c2a8;border-radius:4px;background:#fffaf3';
    gate.innerHTML =
      '<label for="fy-turkey-pickup" style="display:block;font-weight:800;margin-bottom:8px">Choose your Thanksgiving pickup</label>' +
      '<select id="fy-turkey-pickup" required style="display:block;width:100%;min-height:44px;padding:9px;border:1px solid #8c7765;border-radius:3px;background:#fff">' +
        '<option value="">Select a pickup window…</option>' +
        '<option value="2026-11-25">Wednesday, November 25 • 3:00–6:00 PM</option>' +
        '<option value="2026-11-26">Thursday, November 26 • 8:00–11:00 AM</option>' +
      '</select>' +
      '<small id="fy-turkey-pickup-status" style="display:block;margin-top:8px">Pickup is available only during these Thanksgiving preorder windows.</small>';
    primaryForm.parentNode.insertBefore(gate, primaryForm);

    gate.querySelector('select').addEventListener('change', function (event) {
      var iso = event.target.value;
      var option = pickupOptions[iso];
      selected = option ? { iso: iso, dateLabel: option.dateLabel, window: option.window } : null;
      if (selected) {
        applyPickupProperties();
        showStatus('Pickup selected: ' + selected.dateLabel + ', ' + selected.window + '.', false);
      } else {
        showStatus('Choose a pickup window before reserving your turkey.', true);
      }
    });
    return true;
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.action.includes('/cart/add')) return;
    if (!selected) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showStatus('Choose a pickup window before reserving your turkey.', true);
      document.getElementById('fy-turkey-pickup-gate')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    applyPickupProperties();
  }, true);

  document.addEventListener('click', function (event) {
    var button = event.target.closest('button');
    if (!button || !/reserve|add to cart|^add$/i.test((button.textContent || '').trim())) return;
    if (!selected) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showStatus('Choose a pickup window before reserving your turkey.', true);
      document.getElementById('fy-turkey-pickup-gate')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, true);

  if (!mount()) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (mount() || attempts >= 40) clearInterval(timer);
    }, 250);
  }
})();


/* The Thanksgiving picker provides its own live status; hide the theme's generic pickup warning. */
(function () {
  if (location.pathname !== '/products/thanksgiving-whole-turkey-preorder') return;
  function hideNote() {
    var note = document.querySelector('[data-fy-pickup-variant-note]');
    if (!note) return false;
    note.hidden = true;
    note.style.setProperty('display', 'none', 'important');
    return true;
  }
  if (!hideNote()) {
    var tries = 0;
    var timer = setInterval(function () {
      tries += 1;
      if (hideNote() || tries >= 40) clearInterval(timer);
    }, 250);
  }
})();


/* Homepage Thanksgiving turkey preorder feature. */
(function () {
  if (location.pathname !== '/' || window.__FY_TURKEY_HOME_FEATURE__) return;
  window.__FY_TURKEY_HOME_FEATURE__ = true;

  function mountTurkeyHomeFeature() {
    if (document.getElementById('fy-turkey-home-feature')) return true;
    var gameDay = document.getElementById('fy-game-day-home-feature');
    var collection = Array.from(document.querySelectorAll('.shopify-section')).find(function (section) {
      return /what are you craving/i.test(section.textContent || '');
    });
    var target = gameDay || collection;
    if (!target || !target.parentNode) return false;

    if (!document.getElementById('fy-turkey-home-style')) {
      var style = document.createElement('style');
      style.id = 'fy-turkey-home-style';
      style.textContent = [
        '#fy-turkey-home-feature{background:#f4eadb;color:#2b170f;padding:28px 20px}',
        '#fy-turkey-home-feature .fy-turkey-home-inner{max-width:1100px;margin:0 auto;display:grid;grid-template-columns:minmax(240px,420px) 1fr;gap:34px;align-items:center}',
        '#fy-turkey-home-feature img{width:100%;aspect-ratio:1/1;object-fit:cover;border-radius:4px}',
        '#fy-turkey-home-feature .fy-turkey-kicker{text-transform:uppercase;letter-spacing:.13em;font-size:.82rem;font-weight:800;color:#99431f}',
        '#fy-turkey-home-feature h2{margin:7px 0 10px;font-size:clamp(2rem,5vw,3.5rem);line-height:1;text-transform:uppercase}',
        '#fy-turkey-home-feature p{font-size:1.05rem;line-height:1.55;margin:0 0 13px}',
        '#fy-turkey-home-feature .fy-turkey-price{font-size:1.25rem;font-weight:800;margin-bottom:18px}',
        '#fy-turkey-home-feature a{display:inline-block;background:#9c3f1c;color:#fff;text-decoration:none;padding:14px 22px;border-radius:3px;font-weight:800;text-transform:uppercase;letter-spacing:.05em}',
        '@media(max-width:700px){#fy-turkey-home-feature .fy-turkey-home-inner{grid-template-columns:1fr;gap:22px}#fy-turkey-home-feature{padding:22px 16px}}'
      ].join('');
      document.head.appendChild(style);
    }

    var section = document.createElement('section');
    section.id = 'fy-turkey-home-feature';
    section.innerHTML =
      '<div class="fy-turkey-home-inner">' +
        '<a href="/products/thanksgiving-whole-turkey-preorder" aria-label="View Thanksgiving Whole Turkey preorder"><img src="//forkyeahsmokehouse.com/cdn/shop/files/IMG_8765.jpg?v=1788195968&width=900" alt="Thanksgiving Whole Turkey - Preorder" loading="lazy"></a>' +
        '<div>' +
          '<div class="fy-turkey-kicker">Thanksgiving preorder • Limited quantities</div>' +
          '<h2>Let us handle the turkey</h2>' +
          '<p>Choose oak-smoked or deep-fried. Whole 12–14 lb turkey, fully cooked and ready for your Thanksgiving table.</p>' +
          '<p><strong>Pickup:</strong> November 25, 3–6 PM or November 26, 8–11 AM.</p>' +
          '<div class="fy-turkey-price">From $109</div>' +
          '<a href="/products/thanksgiving-whole-turkey-preorder">Choose turkey &amp; pickup</a>' +
        '</div>' +
      '</div>';
    target.parentNode.insertBefore(section, target);
    return true;
  }

  if (!mountTurkeyHomeFeature()) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (mountTurkeyHomeFeature() || attempts >= 40) clearInterval(timer);
    }, 250);
  }
})();


/* Keep the Game Day Pack as the first feature on the homepage. */
(function () {
  if (location.pathname !== '/' || window.__FY_GAME_DAY_TOP_POSITION__) return;
  window.__FY_GAME_DAY_TOP_POSITION__ = true;

  function moveGameDayToTop() {
    var feature = document.getElementById('fy-game-day-home-feature');
    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!feature || !main) return false;
    if (main.firstElementChild !== feature) main.insertBefore(feature, main.firstElementChild);
    return true;
  }

  if (!moveGameDayToTop()) {
    var attempts = 0;
    var timer = setInterval(function () {
      attempts += 1;
      if (moveGameDayToTop() || attempts >= 40) clearInterval(timer);
    }, 250);
  }
})();


(function(){
  function restoreGameDay(){
      if(location.pathname!=='/'||document.getElementById('fy-game-day-home-restore')) return;
          var target=document.getElementById('fy-turkey-home-feature');
              if(!target||!target.parentNode) return;
              if(!target||!target.parentNode) return;
                  var s=document.createElement('section');
                      s.id='fy-game-day-home-restore';
                          s.style.cssText='background:#21120d;color:#f7ead7;padding:42px 20px;text-align:center;border-top:1px solid #6f3f29;border-bottom:1px solid #6f3f29';
                              s.innerHTML='<div style="max-width:900px;margin:0 auto"><div style="color:#e18440;font-weight:700;letter-spacing:.14em;font-size:13px">NEW • PREORDER ONLY</div><h2 style="margin:10px 0;font-size:clamp(34px,5vw,62px);line-height:1">GAME DAY PACK</h2><p style="max-width:720px;margin:0 auto 14px;font-size:18px;line-height:1.5">Feed 6–8 with 1.5 lb brisket, 1.5 lb pulled pork, a full rack of ribs, three quart-size sides, six cornbreads, and a pint of sauce.</p><div style="font-size:24px;font-weight:700;margin:8px 0 20px">$169</div><a href="/products/game-day-pack-feeds-6-8" style="display:inline-block;background:#d36f27;color:#fff;padding:15px 22px;text-decoration:none;font-weight:700">CHOOSE PICKUP DATE &amp; SAUCE</a></div>';
                                  target.parentNode.insertBefore(s,target);
                                    }
                                      if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',restoreGameDay,{once:true}); else restoreGameDay();
                                          [250,500,1000,2000].forEach(function(delay){setTimeout(restoreGameDay,delay);});

                                          (function(){
                                            if(location.pathname!=='/products/thanksgiving-whole-turkey-preorder') return;
                                              var style=document.createElement('style');
                                                style.textContent='#fy-turkey-pickup-gate{background:#2b1711!important;color:#f7ead7!important;border:1px solid #6f3f29!important;border-radius:8px!important;padding:18px!important}#fy-turkey-pickup-gate label,#fy-turkey-pickup-gate>div,#fy-turkey-pickup-gate p{color:#f7ead7!important}#fy-turkey-pickup{background:#3a2118!important;color:#fff3df!important;border:1px solid #b9652d!important}';
                                                  document.head.appendChild(style);
                                                  })();
                                                  

                                                  (function(){
                                                    var path=location.pathname;
                                                      var guarded=['/products/game-day-pack-feeds-6-8','/products/thanksgiving-whole-turkey-preorder'];

(function(){
  function addFacebookReviewLink(){
    if(document.getElementById('fy-facebook-review')) return;
    var footer=document.querySelector('footer')||document.querySelector('[class*=footer]');
    if(!footer) return;
    var section=document.createElement('section');
    section.id='fy-facebook-review';
    section.setAttribute('aria-label','Facebook reviews');
    section.style.cssText='text-align:center;padding:32px 20px;background:#21120d;color:#f4dfbd;';
    section.innerHTML='<div style="font-size:22px;font-weight:700;margin-bottom:8px">Enjoyed your Fork Yeah! BBQ?</div><div style="margin-bottom:18px">Your recommendation helps other local BBQ lovers find us.</div><a href="https://www.facebook.com/profile.php?id=61591718402159&sk=reviews" target="_blank" rel="noopener noreferrer" style="display:inline-block;background:#d96c1f;color:#fff;text-decoration:none;font-weight:700;padding:13px 22px;border-radius:4px">REVIEW US ON FACEBOOK</a>';
    footer.parentNode.insertBefore(section,footer);
  }
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',addFacebookReviewLink,{once:true}); else addFacebookReviewLink();
  setTimeout(addFacebookReviewLink,750);
})();

                                                        function lockQuickAdd(){
                                                            if(guarded.indexOf(path)<0) return;
                                                                var adds=Array.prototype.filter.call(document.querySelectorAll('button'),function(b){return b.textContent.trim().toUpperCase()==='ADD TO CART';});
                                                                    if(adds.length){var b=adds[0];b.disabled=true;b.setAttribute('aria-disabled','true');b.title='Complete the selections on the product page first';b.style.display='none';}
                                                                      }
                                                                        function helper(form,text){
                                                                            var id='fy-required-choice-help',el=document.getElementById(id);
                                                                                if(!el){el=document.createElement('div');el.id=id;el.style.cssText='margin-top:10px;padding:10px 12px;border-radius:6px;background:#3a2118;color:#fff3df;font-weight:700;text-align:center';form.appendChild(el);}
                                                                                    el.textContent=text;return el;
                                                                                      }
                                                                                        function lockReserve(){
                                                                                            var date,sauce,ready=false,message='',form,button;
                                                                                                if(path==='/products/game-day-pack-feeds-6-8'){date=document.getElementById('fy-pack-date');sauce=document.getElementById('fy-pack-sauce');if(!date||!sauce)return;ready=!!date.value&&!!sauce.value;message=ready?'Pickup date and sauce selected — ready to reserve.':'Choose a pickup date and sauce to reserve.';
                                                                                                  document.addEventListener('click',function(ev){if(guarded.indexOf(location.pathname)<0)return;var b=ev.composedPath&&ev.composedPath().find(function(n){return n&&n.tagName==='BUTTON';});if(b&&b.textContent.trim().toUpperCase()==='ADD TO CART'){ev.preventDefault();ev.stopImmediatePropagation();var target=document.getElementById('fy-pack-date')||document.getElementById('fy-turkey-pickup')||document.querySelector('meal-builder');if(target&&target.scrollIntoView)target.scrollIntoView({behavior:'smooth',block:'center'});}},true);
                                                                                                  var loading=Array.prototype.find.call(form.querySelectorAll('*'),function(n){return n.children.length===0&&n.textContent.trim()==='Loading available preorder dates…';});if(loading&&date.options.length>1)loading.textContent='Available preorder dates are shown above.';}
                                                                                                    else if(path==='/products/thanksgiving-whole-turkey-preorder'){date=document.getElementById('fy-turkey-pickup');if(!date)return;ready=!!date.value;message=ready?'Thanksgiving pickup selected — ready to reserve.':'Choose a Thanksgiving pickup window to reserve.';form=document.querySelector('[id^="BuyButtons-ProductForm"]')||date.parentElement;
                                                                                                      setInterval(function(){lockQuickAdd();lockReserve();},1000);

                                                                                                    }
                                                                                                        else return;
                                                                                                            button=Array.prototype.find.call(form.querySelectorAll('button'),function(b){return b.textContent.trim().toUpperCase()==='RESERVE';});if(!button)return;
                                                                                                                if(button.disabled===ready)button.disabled=!ready;button.setAttribute('aria-disabled',ready?'false':'true');button.style.opacity=ready?'1':'.5';button.style.cursor=ready?'pointer':'not-allowed';helper(form,message);
                                                                                                                  }
                                                                                                                    function addPreordersMenu(){
                                                                                                                        return;var main=document.getElementById('MainContent');if(!main||!main.firstElementChild)return;var s=document.createElement('section');s.id='fy-preorders-menu';s.style.cssText='background:#2b1711;color:#f7ead7;padding:38px 20px;text-align:center;border-bottom:1px solid #6f3f29';s.innerHTML='<div style="max-width:1000px;margin:auto"><div style="color:#e18440;font-weight:700;letter-spacing:.14em;font-size:13px">PLAN AHEAD</div><h2 style="margin:8px 0 20px;font-size:clamp(32px,5vw,54px)">SPECIALS</h2><div style="display:flex;gap:14px;justify-content:center;flex-wrap:wrap"><a href="/products/game-day-pack-feeds-6-8" style="min-width:250px;background:#d36f27;color:white;padding:17px 22px;text-decoration:none;font-weight:700">GAME DAY PACK — FEEDS 6–8</a><a href="/products/thanksgiving-whole-turkey-preorder" style="min-width:250px;background:#6f3f29;color:white;padding:17px 22px;text-decoration:none;font-weight:700">THANKSGIVING TURKEY</a></div></div>';main.insertBefore(s,main.firstElementChild);
                                                                                                                          }
                                                                                                                            function apply(){lockQuickAdd();lockReserve();addPreordersMenu();}
                                                                                                                              if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',apply,{once:true});else apply();
                                                                                                                                [250,500,1000,2000,4000].forEach(function(d){setTimeout(apply,d);});
                                                                                                                                })();

                                        })();

                                        (function(){
                                          function addYelpReviewLink(){
                                              if(document.getElementById('fy-yelp-review-link')) return;
                                                  var facebookLink=document.querySelector('a[href*="facebook.com"]');
                                                      if(!facebookLink||!facebookLink.parentNode) return;
                                                          var yelpLink=facebookLink.cloneNode(true);
                                                              yelpLink.id='fy-yelp-review-link';
                                                                  yelpLink.href='https://yelp.to/ge3s8EQXJD';
                                                                      yelpLink.textContent='Review us on Yelp';
                                                                          yelpLink.setAttribute('aria-label','Review Fork Yeah! Smokehouse on Yelp');
                                                                              yelpLink.setAttribute('target','_blank');
                                                                                  yelpLink.setAttribute('rel','noopener noreferrer');
                                                                                      facebookLink.insertAdjacentElement('afterend',yelpLink);
                                                                                        }
                                                                                          addYelpReviewLink();
                                                                                            document.addEventListener('DOMContentLoaded',addYelpReviewLink);
                                                                                              new MutationObserver(addYelpReviewLink).observe(document.documentElement,{childList:true,subtree:true});
                                                                                              })();

/* The Big Krispie: Daily Pit pickup-date gate. */
;(function(){
if(!/^\/products\/the-big-krispie\/?$/.test(location.pathname))return;
var tries=0;function init(){
if(document.getElementById('fy-krispie-pickup'))return;
var form=Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(function(f){return f.querySelector('button[name="add"],button[type="submit"]')});
if(!form){if(tries++<80)setTimeout(init,250);return}
var add=form.querySelector('button[name="add"],button[type="submit"]'),root=document.createElement('div');
root.id='fy-krispie-pickup';root.style.cssText='margin:1rem 0;padding:1rem;border:1px solid rgba(211,111,39,.55);border-radius:8px;background:rgba(211,111,39,.08)';
root.innerHTML='<strong style="display:block;margin-bottom:.45rem">Choose your pickup date</strong><label for="fy-krispie-date" style="display:block;margin-bottom:.3rem">Available Friday–Sunday pickup days</label><select id="fy-krispie-date" required style="width:100%;padding:.75rem;background:#fff;color:#24130d;border:1px solid #9a5a31;border-radius:4px"><option value="">Loading available dates…</option></select><div id="fy-krispie-status" role="status" style="margin-top:.45rem;font-size:.9rem">Select a date before adding this item to your cart.</div>';
form.parentNode.insertBefore(root,form);var pick=root.querySelector('select'),status=root.querySelector('[role="status"]');
function prop(name,value){var x=form.querySelector('input[name="'+name+'"]');if(!x){x=document.createElement('input');x.type='hidden';x.name=name;form.appendChild(x)}x.value=value}
function sync(){var v=pick.value||'';prop('properties[Pickup Date]',v);prop('properties[_Pit Inventory Date]',v);if(v)sessionStorage.setItem('fyPickupDate',v);status.textContent=v?'Pickup date selected — ready to add to cart.':'Select a date before adding this item to your cart.';if(add){add.disabled=!v;add.setAttribute('aria-disabled',v?'false':'true')}if(v)fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes:{'Pickup Date':v}})}).catch(function(){})}
pick.addEventListener('change',sync);form.addEventListener('submit',function(e){sync();if(!pick.value){e.preventDefault();e.stopImmediatePropagation();pick.focus()}},true);if(add)add.addEventListener('click',function(e){if(!pick.value){e.preventDefault();e.stopImmediatePropagation();pick.focus()}},true);if(add){add.disabled=true;add.setAttribute('aria-disabled','true')}
fetch('/apps/pit-inventory',{cache:'no-store',headers:{Accept:'application/json'}}).then(function(r){if(!r.ok)throw Error();return r.json()}).then(function(data){var now=new Date();now.setHours(0,0,0,0);var dates=Object.keys(data.dates||{}).sort().filter(function(v){return new Date(v+'T12:00:00')>=now});pick.innerHTML='<option value="">Select an available pickup date…</option>';dates.forEach(function(v){var o=document.createElement('option'),d=new Date(v+'T12:00:00');o.value=v;o.textContent=d.toLocaleDateString(undefined,{weekday:'long',month:'long',day:'numeric'});pick.appendChild(o)});var saved=sessionStorage.getItem('fyPickupDate')||'';if(dates.indexOf(saved)>=0)pick.value=saved;if(!dates.length){pick.disabled=true;status.textContent='No pickup dates are currently available.'}sync()}).catch(function(){pick.innerHTML='<option value="">Pickup dates unavailable</option>';pick.disabled=true;status.textContent='Pickup dates could not be loaded. Please refresh and try again.';sync()})}
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
})();

/* Big Krispie: bypass the legacy product-button interceptor after date selection. */
;(function(){if(!/^\/products\/the-big-krispie\/?$/.test(location.pathname))return;var n=0;function bind(){var pick=document.getElementById('fy-krispie-date'),form=pick&&pick.closest('#fy-krispie-pickup').nextElementSibling;if(!form||!form.matches('form[action*="/cart/add"]'))form=Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(function(f){return f.querySelector('button[name="add"],button[type="submit"]')});var add=form&&form.querySelector('button[name="add"],button[type="submit"]');if(!pick||!form||!add){if(n++<80)setTimeout(bind,250);return}if(window.__fyKrispieDirectAdd)return;window.__fyKrispieDirectAdd=true;window.addEventListener('click',function(e){var b=e.target&&e.target.closest&&e.target.closest('button[name="add"],button[type="submit"]');if(b!==add)return;e.preventDefault();e.stopImmediatePropagation();if(!pick.value){pick.focus();return}add.disabled=true;var old=add.textContent;add.textContent='ADDING…';var fd=new FormData(form);fetch('/cart/add.js',{method:'POST',headers:{Accept:'application/json'},body:fd}).then(function(r){if(!r.ok)throw Error();return r.json()}).then(function(){location.href='/cart'}).catch(function(){add.disabled=false;add.textContent=old;var s=document.getElementById('fy-krispie-status');if(s)s.textContent='Could not add this item. Please refresh and try again.'})},true)}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',bind);else bind()})();

/* Big Krispie: reliable cart link unaffected by the legacy button blocker. */
;(function(){if(!/^\/products\/the-big-krispie\/?$/.test(location.pathname))return;var tries=0;function bind(){if(document.getElementById('fy-krispie-cart-link'))return;var pick=document.getElementById('fy-krispie-date'),form=pick&&Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(function(f){return f.querySelector('button[name="add"],button[type="submit"]')}),add=form&&form.querySelector('button[name="add"],button[type="submit"]');if(!pick||!form||!add){if(tries++<80)setTimeout(bind,250);return}var link=document.createElement('button');link.id='fy-krispie-cart-link';link.textContent='ADD TO CART';link.style.cssText='display:block;width:100%;box-sizing:border-box;margin-top:.75rem;padding:15px 22px;background:#d36f27;color:#fff;text-align:center;text-decoration:none;font-weight:700;letter-spacing:.04em;border-radius:3px';add.style.display='none';add.insertAdjacentElement('afterend',link);function update(){var id=form.querySelector('[name="id"]'),qty=form.querySelector('[name="quantity"]'),date=pick.value||'';link.setAttribute('aria-disabled',date?'false':'true');link.style.opacity=date?'1':'.55';link.href=date&&id?'/cart/add?id='+encodeURIComponent(id.value)+'&quantity='+encodeURIComponent(qty&&qty.value||1)+'&properties%5BPickup%20Date%5D='+encodeURIComponent(date)+'&properties%5B_Pit%20Inventory%20Date%5D='+encodeURIComponent(date):'#'}pick.addEventListener('change',update);form.addEventListener('change',update);link.addEventListener('click',function(e){update();if(!pick.value){e.preventDefault();pick.focus()}});update()}if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',bind);else bind()})();
99999

/* Desserts and sauces: Daily Pit pickup date plus reliable cart link. */
;(function(){
var match=location.pathname.match(/^\/products\/(banana-pudding|original-sauce|forking-hot-sauce|fresh-house-made-chimichurri)\/?$/);if(!match)return;var tries=0;
function init(){if(document.getElementById('fy-extra-pickup'))return;var form=Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(function(f){return f.querySelector('button[name="add"],button[type="submit"]')}),add=form&&form.querySelector('button[name="add"],button[type="submit"]');if(!form||!add){if(tries++<80)setTimeout(init,250);return}
var root=document.createElement('div');root.id='fy-extra-pickup';root.style.cssText='margin:1rem 0;padding:1rem;border:1px solid rgba(211,111,39,.55);border-radius:8px;background:rgba(211,111,39,.08)';root.innerHTML='<strong style="display:block;margin-bottom:.45rem">Choose your pickup date</strong><label for="fy-extra-date" style="display:block;margin-bottom:.3rem">Available Friday–Sunday pickup days</label><select id="fy-extra-date" required style="width:100%;padding:.75rem;background:#fff;color:#24130d;border:1px solid #9a5a31;border-radius:4px"><option value="">Loading available dates…</option></select><div id="fy-extra-status" role="status" style="margin-top:.45rem;font-size:.9rem">Select a date before adding this item to your cart.</div>';form.parentNode.insertBefore(root,form);
var pick=root.querySelector('select'),status=root.querySelector('[role="status"]'),link=document.createElement('button');link.id='fy-extra-cart-link';link.textContent='ADD TO CART';link.style.cssText='display:block;width:100%;box-sizing:border-box;margin-top:.75rem;padding:15px 22px;background:#d36f27;color:#fff;text-align:center;text-decoration:none;font-weight:700;letter-spacing:.04em;border-radius:3px';add.style.display='none';add.insertAdjacentElement('afterend',link);
function prop(name,value){var x=form.querySelector('input[name="'+name+'"]');if(!x){x=14111411
  .createElement('input');x.type='hidden';x.name=name;form.appendChild(x)}x.value=value}
function update(){var id=form.querySelector('[name="id"]'),qty=form.querySelector('[name="quantity"]'),date=pick.value||'';prop('properties[Pickup Date]',date);prop('properties[_Pit Inventory Date]',date);link.setAttribute('aria-disabled',date?'false':'true');link.style.opacity=date?'1':'.55';link.href=date&&id?'/cart/add?id='+encodeURIComponent(id.value)+'&quantity='+encodeURIComponent(qty&&qty.value||1)+'&properties%5BPickup%20Date%5D='+encodeURIComponent(date)+'&properties%5B_Pit%20Inventory%20Date%5D='+encodeURIComponent(date):'#';status.textContent=date?'Pickup date selected — ready to add to cart.':'Select a date before adding this item to your cart.';if(date){sessionStorage.setItem('fyPickupDate',date);fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({attributes:{'Pickup Date':date}})}).catch(function(){})}}
pick.addEventListener('change',update);form.addEventListener('change',update);link.addEventListener('click',function(e){update();if(!pick.value){e.preventDefault();pick.focus()}});
fetch('/apps/pit-inventory',{cache:'no-store',headers:{Accept:'application/json'}}).then(function(r){if(!r.ok)throw Error();return r.json()}).then(function(data){var now=new Date();now.setHours(0,0,0,0);var dates=Object.keys(data.dates||{}).sort().filter(function(v){return new Date(v+'T12:00:00')>=now});pick.innerHTML='<option value="">Select an available pickup date…</option>';dates.forEach(function(v){var o=document.createElement('option'),d=new Date(v+'T12:00:00');o.value=v;o.textContent=d.toLocaleDateString(undefined,{weekday:'long',month:'long',day:'numeric'});pick.appendChild(o)});var saved=sessionStorage.getItem('fyPickupDate')||'';if(dates.indexOf(saved)>=0)pick.value=saved;if(!dates.length){pick.disabled=true;status.textContent='No pickup dates are currently available.'}update()}).catch(function(){pick.innerHTML='<option value="">Pickup dates unavailable</option>';pick.disabled=true;status.textContent='Pickup dates could not be loaded. Please refresh and try again.';update()})}
if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init);else init();
})();


/* Prevent à-la-carte pit meats from exceeding the selected day's live inventory. */
;(function(){
if(window.__FY_ALA_GUARD__)return;window.__FY_ALA_GUARD__=true;
var P={brisket:{k:'brisket',n:'USDA Prime Brisket',u:'lb',p:2},'tri-tip':{k:'triTip',n:'Argentine Oak-Fired Tri-Tip',u:'lb',p:2},'pulled-pork':{k:'pulledPork',n:'Pulled Pork',u:'lb',p:2},ribs:{k:'ribs',n:'St. Louis Ribs',u:'rack',p:3},'slow-smoked-chicken':{k:'chicken',n:'Slow Smoked Chicken',u:'piece',p:1}},m=location.pathname.match(/^\/products\/([^/?#]+)\/?$/),p=m&&P[m[1]];if(!p)return;
var f,q,b,d,s,bypass=false,busy=false,t;
function iso(v){v=String(v||'').trim();if(/^\d{4}-\d{2}-\d{2}$/.test(v))return v;var x=new Date(v);return isNaN(x)?'':[x.getFullYear(),String(x.getMonth()+1).padStart(2,'0'),String(x.getDate()).padStart(2,'0')].join('-')}
function key(v){v=String(v||'').toLowerCase();if(v==='fy-brisket'||v.indexOf('brisket')>=0)return'brisket';if(v==='fy-tritip'||v.indexOf('tri-tip')>=0||v.indexOf('tri tip')>=0)return'triTip';if(v==='fy-pork'||v==='fy-pulled-pork'||v.indexOf('pulled pork')>=0)return'pulledPork';if(v==='fy-ribs'||v.indexOf('rib')>=0)return'ribs';if(v==='fy-chicken'||v.indexOf('chicken')>=0)return'chicken';return''}
function used(cart,date,day){var total=0;(cart.items||[]).forEach(function(i){var x=i.properties||{},id=iso(x['_Pit Inventory Date']||x['Pickup Date']||x['Pickup day']);if(id!==date)return;var explicit=Number(x['_Pit Inventory '+p.k+' Parts']);if(Number.isFinite(explicit)){total+=explicit*Number(i.quantity||1);return}var meal=0;Object.keys(x).forEach(function(n){if(/^Meat \d+$/i.test(n)&&key(x[n])===p.k)meal+=Number(day.mealParts||1)});if(meal){total+=meal*Number(i.quantity||1);return}if(key(i.sku)===p.k||key(i.product_title||i.title)===p.k)total+=Number(day.alaCarteParts||p.p)*Number(i.quantity||1)});return total}
function note(x,e){if(!s)return;s.textContent=x;s.style.color=e?'#b42318':'inherit';s.style.fontWeight=e?'700':'600'}
function block(x){if(!b)return;b.disabled=x;b.setAttribute('aria-disabled',x?'true':'false')}
async function check(loud){if(busy)return false;var date=iso(d&&d.value);if(!date){block(true);note('Choose a pickup date before selecting this item.',false);return false}busy=true;try{var z=await Promise.all([fetch('/apps/pit-inventory',{cache:'no-store',headers:{Accept:'application/json'}}).then(function(r){if(!r.ok)throw Error();return r.json()}),fetch('/cart.js',{cache:'no-store',headers:{Accept:'application/json'}}).then(function(r){if(!r.ok)throw Error();return r.json()})]),day=z[0].dates&&z[0].dates[date]&&z[0].dates[date][p.k];if(!day){block(true);note(p.n+' is not available for that pickup day.',true);return false}var per=Number(day.alaCarteParts||p.p),max=Math.max(0,Math.floor((Math.max(0,Number(day.remainingParts||0)-used(z[1],date,day)))/per)),want=Math.max(1,Number(q.value||1));q.max=String(max);q.setAttribute('aria-valuemax',String(max));if(max<1){block(true);note(p.n+' is sold out for that pickup day.',true);return false}if(want>max){block(true);note('Only '+max+' '+p.u+(max===1?'':'s')+' of '+p.n+' remain for that pickup day.',true);return false}block(false);note(max+' '+p.u+(max===1?'':'s')+' available for this pickup day.',false);return true}catch(e){block(true);note(loud?"We couldn't verify daily inventory. Please refresh and try again.":'Checking daily inventory…',!!loud);return false}finally{busy=false}}
function later(){clearTimeout(t);t=setTimeout(function(){check(false)},100)}
async function click(e){if(bypass||!b||!e.target.closest||e.target.closest('button')!==b)return;e.preventDefault();e.stopImmediatePropagation();if(!await check(true)){s&&s.scrollIntoView({behavior:'smooth',block:'center'});return}bypass=true;try{b.click()}finally{bypass=false}}
async function submit(e){if(bypass||e.target!==f)return;e.preventDefault();e.stopImmediatePropagation();if(!await check(true)){s&&s.scrollIntoView({behavior:'smooth',block:'center'});return}bypass=true;try{f.requestSubmit(b)}finally{bypass=false}}
function init(){d=document.getElementById('fy-pickup-date');f=Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(function(x){return x.querySelector('input[name="quantity"],input[type="number"]')&&x.querySelector('button[name="add"],button[type="submit"]')});q=f&&(f.querySelector('input[name="quantity"]')||f.querySelector('input[type="number"]'));b=f&&f.querySelector('button[name="add"],button[type="submit"]');if(!d||!f||!q||!b){setTimeout(init,250);return}s=document.getElementById('fy-ala-inventory-status');if(!s){s=document.createElement('div');s.id='fy-ala-inventory-status';s.setAttribute('role','status');s.style.cssText='margin:.65rem 0 0;font-size:.95rem;line-height:1.4';d.insertAdjacentElement('afterend',s)}d.addEventListener('change',later);q.addEventListener('input',later);q.addEventListener('change',later);document.addEventListener('click',click,true);document.addEventListener('submit',submit,true);later()}
document.readyState==='loading'?document.addEventListener('DOMContentLoaded',init):init();
})();
